import React, { useState } from 'react'
import { orderBy, first, isEmpty } from 'lodash'
import { IconBulb } from '@tabler/icons-react'

import { formatPercent } from '@/utils/helpers'
import { Accordion } from '@/components/accordions/Accordion'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'

import { ExternalCallIdsLink } from '../../../components/ExternalCallIdsLink'

/**
 * @param {Function} [props.onAgentClick] Called with the infractor when their
 *   name is clicked. Omit it and the name stays plain text, as on the classic
 *   dashboard page; the customizable dashboard passes one to drill into the
 *   criterion drawer narrowed to that agent.
 */
export const TopInfractorsRow = ({ data, channelLabel, channelType, onAgentClick }) => {
  const [expanded, setExpanded] = useState(false)
  const topInfractorsSorted = orderBy(data.top_infractors, 'failing_percent', 'desc')
  const topInfractor = first(topInfractorsSorted)

  if (isEmpty(topInfractor)) {
    return <div>No top infractors</div>
  }

  const infractorName = `${topInfractor.first_name} ${topInfractor.last_name}`
  const failurePercent = formatPercent(topInfractor.failing_percent)

  const columns = [
    { accessor: 'name', label: 'Name' },
    { accessor: 'failing_percent', label: 'Rate', format: formatPercent },
    { accessor: 'calls', label: channelLabel, isSortable: false },
  ]

  const rows = topInfractorsSorted.map((infractor) => {
    const name = `${infractor.first_name} ${infractor.last_name}`
    return {
      ...infractor,
      id: infractor.user_id,
      name: onAgentClick
        ? {
            as: (
              <button
                type="button"
                className="table-link"
                onClick={() => onAgentClick(infractor, name)}
              >
                {name}
              </button>
            ),
            value: name,
          }
        : name,
      calls: {
        as: (
          <ExternalCallIdsLink
            callIds={infractor.failing_call_ids}
            label={infractor.failing_call_ids?.length}
            channelType={channelType}
          />
        ),
        value: infractor.failing_call_ids?.length,
      },
    }
  })

  return (
    <Accordion
      styled
      id="top-infractors"
      expanded={expanded}
      handleToggle={() => setExpanded(!expanded)}
      icon={<IconBulb className="icon-svg status-magical" />}
      title={
        <>
          <strong>{infractorName}</strong> accounts for <strong>{failurePercent}</strong> of the
          failure rate.
        </>
      }
      actions={
        <strong className="no-wrap status-magical">
          {expanded ? 'Collapse' : 'View Infractors'}
        </strong>
      }
    >
      <AdvancedTable
        testid="top-infractors-row-table"
        index="id"
        columns={columns}
        rows={rows}
        defaultOrderBy="failing_percent"
        defaultOrder="desc"
        wrapColumnContent={false}
      />
    </Accordion>
  )
}
